import { useState } from "react";
import ReactModal from "react-modal";
import { Info } from "lucide-react";
import { toast } from "sonner";

import { Close } from "@/components/common/svgs";
import { TranslatedText } from "@/components/common/text";
import { Checkbox, ClearableInput, DeliveryDatePicker, SelectField } from "@/components/common/inputs";
import { LocationDropdown } from "@/components/search";
import { trailerTypes, truckStatuses, TruckStatus } from "@/utils/constants";
import { useLoadFinderStore } from "@/state/loadFinderStore";
import { useTruckListConfig } from "@/hooks/useTruckList";

const FieldLabel = ({ children }) => (
    <span className="block text-[12px] font-medium text-cm-p-text-secondary uppercase mb-1 px-[2px]">{children}</span>
);

const FieldError = ({ children }) => (
    <div className="flex items-center gap-1 mt-1">
        <Info className="w-4 h-4 text-cm-p-on-container-red shrink-0" />
        <span className="text-[15px] text-cm-p-on-container-red">
            <TranslatedText>{children}</TranslatedText>
        </span>
    </div>
);

export const AddTruckModal = ({ isOpen, onClose, onSuccess }) => {
    const { createTruck, fetchTrucks, fetchCompanyTrucks } = useLoadFinderStore();
    const { data: truckListConfig } = useTruckListConfig();
    const truckListEnabled = !!truckListConfig?.enabled;
    const [driverName, setDriverName] = useState("");
    const [truckNumber, setTruckNumber] = useState("");
    const [trailerType, setTrailerType] = useState(trailerTypes[0]);
    const [status, setStatus] = useState(truckStatuses[0]);
    const [currentLocation, setCurrentLocation] = useState(null);
    const [currentLocationQuery, setCurrentLocationQuery] = useState("");
    const [loadDestination, setLoadDestination] = useState(null);
    const [loadDestinationQuery, setLoadDestinationQuery] = useState("");
    const [deliveryDate, setDeliveryDate] = useState("");
    const [markAsMyTruck, setMarkAsMyTruck] = useState(true);
    const [onTruckList, setOnTruckList] = useState(true);
    const [note, setNote] = useState("");
    const [addAnother, setAddAnother] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [truckNumberError, setTruckNumberError] = useState(null);

    // Everything except the "Add another one" preference, which persists across additions.
    const resetForm = () => {
        setDriverName("");
        setTruckNumber("");
        setTrailerType(trailerTypes[0]);
        setStatus(truckStatuses[0]);
        setCurrentLocation(null);
        setCurrentLocationQuery("");
        setLoadDestination(null);
        setLoadDestinationQuery("");
        setDeliveryDate("");
        setMarkAsMyTruck(true);
        setOnTruckList(true);
        setNote("");
        setError(null);
        setTruckNumberError(null);
    };

    const handleClose = () => {
        resetForm();
        setAddAnother(false);
        onClose();
    };

    const isOnRoute = status?.id === TruckStatus.LOADED.id;
    const isOffDuty = status?.id === TruckStatus.OFF_DUTY.id;
    const dateLabel = isOnRoute ? "Delivery date" : isOffDuty ? "Ready date (optional)" : "Available from (optional)";

    // Driver name and truck number are both mandatory. The date is only mandatory on route.
    const hasCoreFields = driverName.trim() && truckNumber.trim() && trailerType;
    const isValid = isOnRoute
        ? hasCoreFields && loadDestination && deliveryDate
        : isOffDuty
          ? hasCoreFields
          : hasCoreFields && currentLocation;

    const handleSubmit = async () => {
        if (!isValid) return;
        setLoading(true);
        setError(null);
        setTruckNumberError(null);

        const location = isOnRoute ? loadDestination?.zip : currentLocation?.zip;

        const payload = isOffDuty
            ? {
                  truckName: truckNumber,
                  driverName: driverName.trim() || null,
                  trailerType: trailerType.id,
                  status: status.id,
                  location: location ?? null,
                  deliveryDate: deliveryDate || null,
                  onTruckList: false,
                  note: null,
                  markAsMyTruck,
              }
            : {
                  truckName: truckNumber,
                  driverName: driverName.trim() || null,
                  trailerType: trailerType.id,
                  status: status.id,
                  location,
                  deliveryDate: deliveryDate || null,
                  markAsMyTruck,
                  ...(truckListEnabled && {
                      onTruckList,
                      note: onTruckList ? note.trim() || null : null,
                  }),
              };

        try {
            const createdTruck = await createTruck(payload);
            toast.success(`Truck ${truckNumber} has been added`, { className: "cm-toast-success", toasterId: "top-right" });
            fetchTrucks();
            fetchCompanyTrucks();
            if (addAnother) {
                // Keep the modal open for the next entry; preserve the "Add another one" choice.
                resetForm();
            } else {
                onSuccess?.(createdTruck.truckId);
                handleClose();
            }
        } catch (err) {
            const errorMessage = typeof err === "string" ? err : "Failed to add truck.";
            if (errorMessage.toLowerCase().includes("already exists")) {
                setTruckNumberError("This truck number is already in use");
            } else {
                setError(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <ReactModal
            isOpen={isOpen}
            onRequestClose={handleClose}
            shouldCloseOnOverlayClick={false}
            ariaHideApp={false}
            overlayClassName="ReactModal__Overlay fixed inset-0 bg-black/70 z-modal flex items-center justify-center"
            className="fixed inset-0 flex items-center justify-center pointer-events-none"
        >
            <div className="bg-cm-p-surface rounded-[12px] w-[540px] flex flex-col pointer-events-auto shadow-xl">
                <div className="flex items-center justify-between p-4 border-b border-cm-p-outline">
                    <h2 className="text-[16px] font-medium text-cm-p-text-primary">
                        <TranslatedText>Enter driver and truck details</TranslatedText>
                    </h2>
                    <button type="button" onClick={handleClose} className="p-1 rounded hover:bg-cm-p-surface-hover">
                        <Close />
                    </button>
                </div>

                <div className="px-4 pt-4 pb-6 flex flex-col gap-5">
                    {/* Driver name (optional) + Truck number */}
                    <div className="flex gap-4 items-start">
                        <div className="flex-1 min-w-0">
                            <FieldLabel>
                                <TranslatedText>Driver name</TranslatedText>
                            </FieldLabel>
                            <ClearableInput value={driverName} onChange={setDriverName} placeholder="Driver name" />
                        </div>

                        <div className="flex-1 min-w-0">
                            <FieldLabel>
                                <TranslatedText>Truck number</TranslatedText>
                            </FieldLabel>
                            <ClearableInput
                                value={truckNumber}
                                onChange={(value) => {
                                    setTruckNumber(value);
                                    if (truckNumberError) setTruckNumberError(null);
                                }}
                                placeholder="000000"
                                error={!!truckNumberError}
                            />
                            {truckNumberError && <FieldError>{truckNumberError}</FieldError>}
                        </div>
                    </div>

                    {/* Trailer type + Status */}
                    <div className="flex gap-4 items-start">
                        <div className="flex-1 min-w-0">
                            <FieldLabel>
                                <TranslatedText>Trailer type</TranslatedText>
                            </FieldLabel>
                            <SelectField value={trailerType} onChange={setTrailerType} options={trailerTypes} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <FieldLabel>
                                <TranslatedText>Status</TranslatedText>
                            </FieldLabel>
                            <SelectField value={status} onChange={setStatus} options={truckStatuses} />
                        </div>
                    </div>

                    {/* Status-conditional location + date row */}
                    <div className="flex gap-4 items-start">
                        {isOnRoute ? (
                            <div className="flex-1 min-w-0">
                                <FieldLabel>
                                    <TranslatedText>Load destination</TranslatedText>
                                </FieldLabel>
                                <LocationDropdown
                                    name="loadDestination"
                                    value={loadDestination}
                                    setValue={setLoadDestination}
                                    query={loadDestinationQuery}
                                    setQuery={setLoadDestinationQuery}
                                    label=""
                                    theme="truck"
                                />
                            </div>
                        ) : (
                            <div className="flex-1 min-w-0">
                                <FieldLabel>
                                    <TranslatedText>{isOffDuty ? "Location (optional)" : "Current location"}</TranslatedText>
                                </FieldLabel>
                                <LocationDropdown
                                    name="currentLocation"
                                    value={currentLocation}
                                    setValue={setCurrentLocation}
                                    query={currentLocationQuery}
                                    setQuery={setCurrentLocationQuery}
                                    label=""
                                    theme="truck"
                                />
                            </div>
                        )}
                        <div className="flex-1 min-w-0 relative">
                            <FieldLabel>
                                <TranslatedText>{dateLabel}</TranslatedText>
                            </FieldLabel>
                            <DeliveryDatePicker value={deliveryDate} onChange={setDeliveryDate} />
                        </div>
                    </div>

                    <div className="flex flex-col gap-3">
                        <Checkbox
                            checked={markAsMyTruck}
                            onChange={setMarkAsMyTruck}
                            label={<TranslatedText>Mark as My Truck</TranslatedText>}
                        />

                        {!isOffDuty && truckListEnabled && (
                            <>
                                <Checkbox
                                    checked={onTruckList}
                                    onChange={setOnTruckList}
                                    label={<TranslatedText>Make it visible on the public Truck List</TranslatedText>}
                                />

                                {onTruckList && (
                                    <div>
                                        <FieldLabel>
                                            <TranslatedText>Note (optional)</TranslatedText>
                                        </FieldLabel>
                                        <ClearableInput value={note} onChange={setNote} placeholder="Enter note" />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <div className="flex flex-col gap-3 p-4 border-t border-cm-p-outline">
                    {error && <p className="text-sm text-red-500 text-center">{error}</p>}
                    <div className="flex items-center gap-3">
                        <div className="flex-1">
                            <Checkbox
                                checked={addAnother}
                                onChange={setAddAnother}
                                label={<TranslatedText>Add another one</TranslatedText>}
                            />
                        </div>
                        <button
                            type="button"
                            onClick={handleClose}
                            className="w-[120px] h-[40px] rounded-[6px] bg-cm-p-surface-container text-cm-p-text-primary text-[14px] font-medium focus:outline-none"
                        >
                            <TranslatedText>Cancel</TranslatedText>
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            className="w-[120px] h-[40px] rounded-[6px] bg-cm-p-accent-500 text-cm-p-neutral-dark-500 text-[14px] font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={loading || !isValid}
                        >
                            <TranslatedText>Add</TranslatedText>
                        </button>
                    </div>
                </div>
            </div>
        </ReactModal>
    );
};
